import React, { useContext, type PropsWithChildren } from 'react';
import { useClients } from '../clients';
import { useABTest } from '../../extensible/ab-test-context';

export const FILTERS_EXPERIMENT_ID = 'filters-experiment';
export const NO_MORE_FASTER_SEARCH_EXPERIMENT_ID = 'peach';
export type ConfluenceFeaturesOverrides = Partial<ConfluenceFeatures>;

export type CurrentSpaceInfo = {
	id: string;
	spaceKey: string;
	spaceName: string;
	iconUrl: string;
};

export interface ConfluenceFeatures {
	isMultiSite?: boolean;
	currentSpace?: CurrentSpaceInfo;
	isFasterSearchDisabled?: boolean;
}

export const DefaultFeatures: ConfluenceFeatures = Object.freeze({
	isMultiSite: false,
});

const ConfluenceFeatureContext = React.createContext<ConfluenceFeatures>(DefaultFeatures);

interface ConfluenceFeaturesProviderProps {
	features?: ConfluenceFeaturesOverrides;
}

export const ConfluenceFeaturesProvider = ({
	children,
	features: featuresOverride,
}: PropsWithChildren<ConfluenceFeaturesProviderProps>) => {
	const { sites = [] } = useClients();
	const abTest = useABTest();
	const value = {
		...DefaultFeatures,
		...{
			isMultiSite: sites.length > 1,
			isFasterSearchDisabled: abTest?.experimentId === NO_MORE_FASTER_SEARCH_EXPERIMENT_ID,
		},
		...featuresOverride,
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const memoedValue = React.useMemo(() => value, [...Object.values(value)]);

	return (
		<ConfluenceFeatureContext.Provider value={memoedValue}>
			{children}
		</ConfluenceFeatureContext.Provider>
	);
};

export const useFeatures = (): ConfluenceFeatures => {
	return useContext(ConfluenceFeatureContext);
};
